import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  getSellerCustomerBase,
  getSellerFinanceSummary,
  getSellerKpi,
  getSellerOrderStatus,
  getSellerSalesTrend,
  getSellerTodoSummary,
  getSellerTopProducts,
} from "services/sellerReportApi";
import {
  SellerCustomerBase,
  SellerFinanceSummary,
  SellerKpi,
  SellerOrderStatus,
  SellerReportPeriod,
  SellerSalesTrend,
  SellerTodoSummary,
  SellerTopProductList,
} from "services/interface/sellerReport.interface";

export const SELLER_KPI_QUERY_KEY = "sellerReportKpi";
export const SELLER_TODO_QUERY_KEY = "sellerReportTodo";
export const SELLER_SALES_TREND_QUERY_KEY = "sellerReportSalesTrend";
export const SELLER_ORDER_STATUS_QUERY_KEY = "sellerReportOrderStatus";
export const SELLER_TOP_PRODUCTS_QUERY_KEY = "sellerReportTopProducts";
export const SELLER_CUSTOMER_BASE_QUERY_KEY = "sellerReportCustomerBase";
export const SELLER_FINANCE_QUERY_KEY = "sellerReportFinance";

// queryClient ถูกสร้างแบบไม่มี default (src/queryClient.ts) — ทุก hook จึงตั้ง staleTime เอง
const PERIOD_STALE_TIME = 60_000;

/**
 * ค่า option ร่วมของ query ที่ผูกกับช่วงเวลา
 * keepPreviousData: สลับ 7 วัน/30 วัน/ปีนี้ แล้วตัวเลขเดิมยังอยู่ (การ์ดทำจางแทนการขึ้น skeleton ใหม่)
 */
const periodQueryOptions = {
  staleTime: PERIOD_STALE_TIME,
  placeholderData: keepPreviousData,
};

/** KPI 4 ช่องบนสุด */
export const useSellerKpi = (period: SellerReportPeriod) =>
  useQuery<SellerKpi, Error>({
    queryKey: [SELLER_KPI_QUERY_KEY, period],
    queryFn: () => getSellerKpi({ period }),
    ...periodQueryOptions,
  });

/**
 * "สิ่งที่ต้องทำวันนี้" — ยอดสด ไม่ผูกกับ period จึงไม่ยิงซ้ำตอนสลับช่วงเวลา
 * dataUpdatedAt ของ query นี้คือที่มาของข้อความ "อัปเดตล่าสุด HH:mm น."
 */
export const useSellerTodoSummary = () =>
  useQuery<SellerTodoSummary, Error>({
    queryKey: [SELLER_TODO_QUERY_KEY],
    queryFn: getSellerTodoSummary,
    staleTime: 30_000,
    refetchInterval: 120_000,
    refetchOnMount: "always",
  });

/** กราฟแนวโน้มยอดขาย */
export const useSellerSalesTrend = (period: SellerReportPeriod) =>
  useQuery<SellerSalesTrend, Error>({
    queryKey: [SELLER_SALES_TREND_QUERY_KEY, period],
    queryFn: () => getSellerSalesTrend({ period }),
    ...periodQueryOptions,
  });

/** โดนัทสถานะออเดอร์ */
export const useSellerOrderStatus = (period: SellerReportPeriod) =>
  useQuery<SellerOrderStatus, Error>({
    queryKey: [SELLER_ORDER_STATUS_QUERY_KEY, period],
    queryFn: () => getSellerOrderStatus({ period }),
    ...periodQueryOptions,
  });

/** สินค้าขายดี 5 อันดับ */
export const useSellerTopProducts = (period: SellerReportPeriod) =>
  useQuery<SellerTopProductList, Error>({
    queryKey: [SELLER_TOP_PRODUCTS_QUERY_KEY, period],
    queryFn: () => getSellerTopProducts({ period }),
    ...periodQueryOptions,
  });

/** การ์ด "โอกาสซื้อซ้ำ" */
export const useSellerCustomerBase = (period: SellerReportPeriod) =>
  useQuery<SellerCustomerBase, Error>({
    queryKey: [SELLER_CUSTOMER_BASE_QUERY_KEY, period],
    queryFn: () => getSellerCustomerBase({ period }),
    ...periodQueryOptions,
  });

/** การ์ด "รอบรับเงิน" — ยอดสด ไม่ผูกกับ period */
export const useSellerFinanceSummary = () =>
  useQuery<SellerFinanceSummary, Error>({
    queryKey: [SELLER_FINANCE_QUERY_KEY],
    queryFn: getSellerFinanceSummary,
    staleTime: 30_000,
    refetchOnMount: "always",
  });
